import { GroupRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ProfileSelection } from 'components/ProfileSelection/ProfileSelection';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { GroupMember } from '../types/types';
import { UpdateGroupsMutationDocument } from './GroupForm';

export const GroupEditorsFormFragment = graphql(`
  fragment GroupEditorsFormFields on Query {
    group: groups(where: { uuid: $groupUuid }) {
      __typename
      uuid
      editors {
        __typename
        uuid
        displayName
        organizationRoles
      }
      viewers {
        __typename
        uuid
      }
    }
    ...ProfileSelectionFields
  }
`);

type GroupEditorsFormType = {
  editors: GroupMember[];
};

type GroupEditorsFormProps = {
  groupUuid: string;
  schoolYearUuid: string;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
  query: FragmentType<typeof GroupEditorsFormFragment> | undefined;
};

export const GroupEditorsForm = ({ groupUuid, schoolYearUuid, onLoad, onClose, query }: GroupEditorsFormProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(GroupEditorsFormFragment, query);

  const group = fragment?.group[0];

  const [, updateGroup] = useMutation(UpdateGroupsMutationDocument);

  const handleSubmit = async (values: GroupEditorsFormType, helpers: FormikHelpers<GroupEditorsFormType>) => {
    onLoad(true);

    const { editors } = values;

    const editorUuids = uniqueByProfileUuid(editors).map((e) => e.profileUuid);

    const { error } = await updateGroup({
      update: { editors: editorUuids },
      uuid: groupUuid,
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  const initialValues: GroupEditorsFormType = {
    editors:
      group?.editors.map((e) => ({
        ...e,
        profileUuid: e.uuid,
        name: e.displayName ?? '',
        groupRole: GroupRoles.Editor,
      })) ?? [],
  };

  return (
    <Formik<GroupEditorsFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ setFieldValue, values }) => (
        <Form>
          <ProfileSelection
            initialMembers={values.editors.map((m) => ({
              ...m,
              uuid: m.profileUuid,
              organizationRoles: m.organizationRoles,
              selected: false,
            }))}
            excludeMemberUuids={group?.viewers.map((v) => v.uuid)}
            onPick={async (picked) => {
              const editors: GroupMember[] = picked.map((p) => ({
                ...p,
                profileUuid: p.uuid,
                groupRole: GroupRoles.Editor,
              }));
              await setFieldValue('editors', editors);
            }}
            categoriesOnly
            schoolYearUuid={schoolYearUuid}
            query={fragment}
          />

          <ModalBottomButtons
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={t('groups.editorsHint')}
          />
        </Form>
      )}
    </Formik>
  );
};
